import { useState } from "react";
import { Pencil, Trash2, Loader2 } from "lucide-react";
import { formatCurrency } from "@vaulzo/core/constants";
import LiabilityDialog from "./LiabilityDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useSaveLiability, useDeleteLiability } from "@vaulzo/core/hooks/usePortfolioMutations";

/**
 * Liabilities page. Owns its dialog state and persists through the
 * portfolio mutation hooks; the parent only provides the list.
 */
export default function Liabilities({ liabilities }) {
  const saveLiability = useSaveLiability();
  const deleteLiability = useDeleteLiability();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingLiability, setEditingLiability] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // liability awaiting confirmation

  const isSaving = saveLiability.isPending;

  const saveWith = async (payload, id, onSuccess) => {
    try {
      await saveLiability.mutateAsync({ payload, id });
      onSuccess();
    } catch {
      /* error toast raised by the mutation hook */
    }
  };

  const confirmDelete = async () => {
    const id = pendingDelete?.id;
    if (!id) return;
    setDeletingId(id);
    try {
      await deleteLiability.mutateAsync(id);
      setPendingDelete(null);
    } catch {
      /* error toast raised by the mutation hook */
    } finally {
      setDeletingId(null);
    }
  };

  const rows = (liabilities || [])
    .map((l) => ({ liab: l, value: l.amount || 0 }))
    .sort((a, b) => b.value - a.value);
  const total = rows.reduce((s, r) => s + r.value, 0);
  const max = rows.length ? rows[0].value : 1;

  return (
    <main className="mx-auto max-w-[800px] px-6 pt-14">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="m-0 text-[26px] font-bold tracking-[-0.02em] text-vaulzo-ink">Liabilities</h1>
        <button
          onClick={() => setShowAddDialog(true)}
          className="flex items-center gap-[7px] rounded-[10px] bg-vaulzo-danger px-[18px] py-[11px] text-sm font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-danger-hover"
        >
          <span className="text-base leading-none">+</span>
          Add liability
        </button>
      </div>

      <div className="flex items-baseline justify-between px-1 pb-2 pt-5 text-[12px] uppercase tracking-[0.04em] text-vaulzo-fainter">
        <span>Liability</span>
        <span>Amount</span>
      </div>

      {rows.length === 0 ? (
        <div className="py-[72px] text-center text-[15px] text-vaulzo-subtle">Nothing here yet.</div>
      ) : (
        rows.map((r, i) => {
          const pct = total ? (r.value / total) * 100 : 0;
          const barW = max ? (r.value / max) * 100 : 0;
          const deleting = deletingId === r.liab.id;
          return (
            <div key={r.liab.id} className="border-t border-vaulzo-row px-1 pb-[15px] pt-4">
              {/* Name + meta stack on the left so long names truncate
                  instead of being crushed by the chips (mobile fix). */}
              <div className="flex items-center gap-3">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] text-vaulzo-ink">{r.liab.name}</span>
                  <span className="mt-[6px] flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="whitespace-nowrap rounded-full bg-vaulzo-row px-[11px] py-[3px] text-[12px] text-vaulzo-muted">
                      {r.liab.category}
                    </span>
                    <span className="num text-[12px] text-vaulzo-subtle">{pct.toFixed(1)}%</span>
                  </span>
                </span>
                <span className="num whitespace-nowrap text-right text-[15px] font-semibold text-vaulzo-danger">
                  {formatCurrency(r.value)}
                </span>
                <span className="flex w-14 justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingLiability(r.liab);
                      setShowEditDialog(true);
                    }}
                    aria-label="Edit"
                    className="flex items-center p-[6px] text-vaulzo-fainter transition-colors hover:text-vaulzo-accent"
                  >
                    <Pencil className="h-[15px] w-[15px]" strokeWidth={1.7} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(r.liab)}
                    disabled={deletingId !== null}
                    aria-label="Delete"
                    className="flex items-center p-[6px] text-vaulzo-bar transition-colors hover:text-vaulzo-danger disabled:opacity-50"
                  >
                    {deleting ? (
                      <Loader2 className="h-[15px] w-[15px] animate-spin" aria-hidden />
                    ) : (
                      <Trash2 className="h-[15px] w-[15px]" strokeWidth={1.7} />
                    )}
                  </button>
                </span>
              </div>
              <div className="mt-3 h-[3px] rounded-sm bg-vaulzo-row">
                <div
                  className="h-[3px] rounded-sm transition-all duration-500 ease-out"
                  style={{ width: `${barW}%`, backgroundColor: i === 0 ? "rgb(var(--vz-danger))" : "rgb(var(--vz-danger-track))" }}
                />
              </div>
            </div>
          );
        })
      )}

      {rows.length > 0 && (
        <div className="mt-2 flex items-baseline justify-between border-t-2 border-vaulzo-ink px-1 pt-[22px]">
          <span className="text-[15px] font-semibold text-vaulzo-ink">Total</span>
          <span className="num text-[22px] font-bold tracking-[-0.02em] text-vaulzo-danger">
            {formatCurrency(total)}
          </span>
        </div>
      )}

      <LiabilityDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={(payload) => saveWith(payload, undefined, () => setShowAddDialog(false))}
        isSubmitting={isSaving}
      />

      <LiabilityDialog
        isOpen={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setEditingLiability(null);
        }}
        onSubmit={(payload) =>
          saveWith(payload, editingLiability.id, () => {
            setShowEditDialog(false);
            setEditingLiability(null);
          })
        }
        editingLiability={editingLiability}
        isSubmitting={isSaving}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete liability?"
        message={pendingDelete ? `"${pendingDelete.name}" will be removed and your net worth will go up accordingly.` : ""}
        busy={deletingId !== null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </main>
  );
}
